import {PublicClientApplication, Logger, LogLevel, InteractionRequiredAuthError} from '@azure/msal-browser';
/**
 * Auth client wrapper for Azure B2C
 */
export class Auth extends PublicClientApplication {
  /**
   * Build auth class
   *
   * @param {object} analytics the azure app insights instance
   */
  constructor(analytics) {
    const redirectUrl = 'https://warehouse.pimsical.app/';

    const logger = new Logger(
        (logLevel, message) => {
          if (analytics && analytics.trackTrace) {
            analytics.trackTrace(message, {}, logLevel);
          }
        }, {
          level: LogLevel.Warning,
          piiLoggingEnabled: false,
        },
    );

    super({
      auth: {
        clientId: '989452dc-c7f6-4ebb-a164-19fc41b5a3eb',
        authority: 'https://peoplevoxintegration.b2clogin.com/peoplevoxintegration.onmicrosoft.com/B2C_1_SignUpAndSignIn',
        knownAuthorities: ['peoplevoxintegration.b2clogin.com'],
        redirectUri: redirectUrl,
      },
      system: {
        logger,
      },
    });

    this._userID = null;
    this.analytics = analytics;
    this.redirectUrl = redirectUrl;

    this.scopes = ['openid', 'email', 'profile', 'https://peoplevoxintegration.onmicrosoft.com/b7f93736-e0ac-4040-a1ba-72ba7bfe27c2/warehouse.access'];
  }

  /**
   * Used to watch for changes to the user
   *
   * @param {Function} callback the callback to trigger
   */
  onUserChange(callback) {
    this.userChangeCallback = callback;
  }

  /**
   * Getter for the remote streams
   *
   * @returns {Array<MediaStream>} the remote streams
   */
  get userID() {
    return this._userID;
  }

  /**
   * Setter for the remote streams,
   * triggers the remote stream change callback if defined
   *
   * @param {Array<MediaStream>} value the new remote streams
   */
  set userID(value) {
    this._userID = value;
    if (this.userChangeCallback) {
      this.userChangeCallback(value);
    }
  }

  /**
   * Get users email
   *
   * @returns {string} the email
   */
  get email() {
    const currentAccounts = this.getAllAccounts();
    return currentAccounts[0]?.username || '';
  }

  /**
   * Grabs an access token for user
   *
   * @returns {Promise} the request
   */
  async loadAccessToken() {
    const currentAccounts = this.getAllAccounts();
    const tokenRequest = {scopes: this.scopes, account: currentAccounts[0]};
    try {
      const token = await this.acquireTokenSilent(tokenRequest);
      if (!token.accessToken || token.accessToken === '') {
        throw new InteractionRequiredAuthError;
      } else {
        return token.accessToken;
      }
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        return this.acquireTokenRedirect(tokenRequest);
      }
      return Promise.reject(e);
    }
  }

  /**
   * Login redirect for auth
   *
   * @returns {Promise} the request
   */
  async login() {
    return this.loginRedirect({scopes: this.scopes});
  }

  /**
   * Logout for auth
   *
   * @returns {Promise} the request
   */
  async logout() {
    const currentAccounts = this.getAllAccounts();
    const tokenRequest = {scopes: this.scopes, account: currentAccounts[0]};
    try {
      const token = await this.acquireTokenSilent(tokenRequest);
      await this.logoutRedirect({
        idTokenHint: token.idToken,
        postLogoutRedirectUri: this.redirectUrl,
      });
      this.userID = null;
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Configure handling the redirect
   */
  setup() {
    this.handleRedirectPromise()
        .then(this.handleRedirect.bind(this))
        .catch((e) => {
          if (this.analytics && this.analytics.trackException) {
            this.analytics.trackException(e);
          }
        });
  }

  /**
   * Handle login redirect
   *
   * @param {object} response the login redirect
   */
  handleRedirect(response) {
    if (response) {
      this.userID = response.account.homeAccountId;
    } else {
      const currentAccounts = this.getAllAccounts();
      if (currentAccounts.length > 1) {
        console.warn('too many users');
      } else if (currentAccounts.length === 1) {
        this.userID = currentAccounts[0].homeAccountId;
      }
    }
  }
}
